import { DeviceRotation, RotationAxis } from '@shure/shared/angular/data-access/system-api/models';

import { CoverageDeviceLocalRotation, CoverageDeviceRoomRotation } from '../api/models';

import {
	applyRoomHeading,
	deviceLocalAxesInRoom,
	extractDeviceLocalEulerFromAxes,
	normalizedDegrees,
	radToDeg,
	type DeviceLocalAxesInRoom,
	type Vec3
} from './dca-device-local-pose-math';

/** FE defaults for body-frame Euler (`features.deviceLocalRotation` / SysAPI `orientation`). */
export const DCA_DEVICE_LOCAL_ROTATION_DEFAULTS: CoverageDeviceLocalRotation = {
	xAxis: 0,
	yAxis: 0,
	zAxis: 0
};

const asDeviceLocal = (rotation: DeviceRotation | CoverageDeviceLocalRotation): CoverageDeviceLocalRotation => ({
	xAxis: rotation.xAxis,
	yAxis: rotation.yAxis ?? 0,
	zAxis: rotation.zAxis
});

const asRoom = (rotation: DeviceRotation | CoverageDeviceRoomRotation): CoverageDeviceRoomRotation => ({
	xAxis: rotation.xAxis,
	yAxis: rotation.yAxis ?? 0,
	zAxis: rotation.zAxis
});

/** Normalize SysAPI/device orientation triple into FE device-local Euler. No pose conversion. */
export const deviceOrientationToDeviceLocalRotation = (
	orientation: DeviceRotation | CoverageDeviceLocalRotation
): CoverageDeviceLocalRotation => asDeviceLocal(orientation);

export const deviceLocalRotationToRoomRotation = (
	deviceLocal: DeviceRotation | CoverageDeviceLocalRotation
): CoverageDeviceRoomRotation => {
	const c = asDeviceLocal(deviceLocal);
	const axes = deviceLocalAxesInRoom(c.xAxis, c.yAxis, c.zAxis, 0);
	return extractRoomEulerFromAxes(axes);
};

export const roomRotationToDeviceLocalRotation = (
	room: DeviceRotation | CoverageDeviceRoomRotation
): CoverageDeviceLocalRotation => {
	const r = asRoom(room);
	// Roll is not a fourth room axis. It is already in room Y/Z, and the inverse recovers it.
	const axes = deviceLocalAxesInRoom(r.yAxis ?? 0, r.zAxis, 0, r.xAxis);
	return extractDeviceLocalEulerFromAxes(axes);
};

/** Set one room axis, return updated device-local rotations */
export const applyRoomRotationAxis = (
	deviceLocal: DeviceRotation | CoverageDeviceLocalRotation,
	axis: RotationAxis,
	angle: number
): CoverageDeviceLocalRotation => {
	const room = deviceLocalRotationToRoomRotation(deviceLocal);
	const nextRoom: CoverageDeviceRoomRotation = {
		xAxis: axis === RotationAxis.XAxis ? angle : room.xAxis,
		yAxis: axis === RotationAxis.YAxis ? angle : (room.yAxis ?? 0),
		zAxis: axis === RotationAxis.ZAxis ? angle : room.zAxis
	};
	return roomRotationToDeviceLocalRotation(nextRoom);
};

export const extractRoomEulerFromAxes = (axes: DeviceLocalAxesInRoom): CoverageDeviceRoomRotation => {
	const led = axes.y;
	const grille = axes.z;
	const ledHorizontal = Math.hypot(led.x, led.y);
	const headingDeg =
		ledHorizontal > 1e-6 ? radToDeg(Math.atan2(led.x, led.y)) : radToDeg(Math.atan2(grille.x, grille.y));

	const removeHeading = (v: Vec3): Vec3 => applyRoomHeading(v, -headingDeg);
	const unheadedAxes: DeviceLocalAxesInRoom = {
		x: removeHeading(axes.x),
		y: removeHeading(axes.y),
		z: removeHeading(axes.z)
	};

	const deviceLocal = extractDeviceLocalEulerFromAxes(unheadedAxes);
	return {
		xAxis: normalizedDegrees(headingDeg),
		yAxis: deviceLocal.xAxis, // tilt
		zAxis: deviceLocal.yAxis // wall-turn
	};
};
